import type { ApiClient } from '@center-technologies/shared-api-clients';
import type { AppDispatch } from '@center-pwa/store/store';
import type { Schema } from 'types/schema';
import type { SchemaTypeStatus } from '@center-pwa/reducers/schemaTypesReducer';
import { batchFulfilled, batchFailed, setLoading } from '@center-pwa/reducers/schemaTypesReducer';

export const COALESCE_WINDOW_MS = 16;
export const MAX_BATCH = 100;

interface PendingEntry {
  names: Set<string>;
  apiClient: ApiClient;
  dispatch: AppDispatch;
  timer: ReturnType<typeof setTimeout> | null;
}

// Keyed by organizationId
const pending = new Map<string, PendingEntry>();

function chunkArray<T>(arr: T[], size: number): T[][] {
  const chunks: T[][] = [];
  for (let i = 0; i < arr.length; i += size) {
    chunks.push(arr.slice(i, i + size));
  }
  return chunks;
}

async function flush(organizationId: string): Promise<void> {
  const entry = pending.get(organizationId);
  if (!entry) return;

  const names = Array.from(entry.names);
  const { apiClient, dispatch } = entry;
  pending.delete(organizationId);

  const chunks = chunkArray(names, MAX_BATCH);
  const now = Date.now();

  await Promise.all(
    chunks.map(async (chunk) => {
      const requestedKeys = chunk.map((name) => `${organizationId}::${name}`);
      try {
        // The organization comes from the client's x-center-organization-id header; schema-service
        // refuses one named in the query (400 organization_not_accepted). `organizationId` here only
        // keys the batch and the cached results.
        const schemas = await (apiClient.get as (path: string, opts: object) => Promise<Schema[]>)(
          '/schemas',
          { params: { systemNames: chunk.join(',') } },
        );
        dispatch(batchFulfilled({ schemas, requestedKeys, fetchedAt: now, organizationId }));
      } catch (err: unknown) {
        const error = err instanceof Error ? err.message : 'Unknown error';
        dispatch(batchFailed({ keys: requestedKeys, error }));
      }
    }),
  );
}

/**
 * Register a schema system_name for batched fetching.
 *
 * Skips names whose current Redux status is already loading/succeeded/notFound.
 * Deduplicates names registered in the same flush window via a Set.
 * Dispatches setLoading immediately so consumers see loading state before the
 * flush fires.
 */
export function register(
  systemName: string,
  organizationId: string,
  apiClient: ApiClient,
  dispatch: AppDispatch,
  currentStatus?: SchemaTypeStatus | null,
): void {
  if (
    currentStatus === 'succeeded' ||
    currentStatus === 'loading' ||
    currentStatus === 'notFound'
  ) {
    return;
  }

  if (!pending.has(organizationId)) {
    pending.set(organizationId, {
      names: new Set(),
      apiClient,
      dispatch,
      timer: null,
    });
  }

  const entry = pending.get(organizationId)!;

  // Within-window dedup: only dispatch setLoading once per name per window
  if (entry.names.has(systemName)) {
    return;
  }

  entry.names.add(systemName);
  dispatch(setLoading({ keys: [`${organizationId}::${systemName}`] }));

  entry.timer ??= setTimeout(() => flush(organizationId), COALESCE_WINDOW_MS);
}

/**
 * Clear all pending entries and timers. Call on org/identity switch so
 * in-flight batches for the previous org don't flush into the new org's
 * context (pairs with the schemaTypes `clearAll` slice reset).
 */
export function resetCoalescer(): void {
  for (const entry of pending.values()) {
    if (entry.timer !== null) {
      clearTimeout(entry.timer);
    }
  }
  pending.clear();
}

/** Test-only alias for {@link resetCoalescer}. */
export function _resetCoalescerForTests(): void {
  resetCoalescer();
}
